import React, { useEffect, useState } from "react";
import ImageSlider, { photoList } from "@/components/ImageSlider";
import { Link, useParams } from "react-router-dom";
import { fetchShop, fetchShopStatus, trackShopContact } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { contactGate } from "@/lib/contactGate";
import { ProductCard } from "@/components/Cards";
import ShopStatusPill from "@/components/ShopStatusPill";
import { MapPin, Star, Phone, Store, Navigation } from "lucide-react";
import BackButton from "@/components/BackButton";
import BusinessProfileSections from "@/components/profile/BusinessProfileSections";
import { toast } from "sonner";


export default function ShopDetail() {
  const { id } = useParams();
  const { pincode, user } = useApp();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchShop(id, pincode).then(setShop).catch(() => {}).finally(() => setLoading(false));
  }, [id, pincode]);

  // Real-time status polling every 25s
  useEffect(() => {
    if (!id) return;
    const tick = async () => {
      try {
        const res = await fetchShopStatus(id);
        setShop((prev) => prev ? { ...prev, status: res.status } : prev);
      } catch { /* transient network error: keep last status, next poll retries */ }
    };
    const iv = setInterval(tick, 25000);
    // Also refresh when tab regains focus
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [id]);

  const openDirections = () => {
    if (!shop?.directions_url) {
      toast.error("Shop location has not been added yet.");
      return;
    }
    // Open Google Maps directly (uses browser location as source automatically)
    window.open(shop.directions_url, "_blank", "noopener");
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-slate-500">Loading...</div>;
  if (!shop) return <div className="max-w-7xl mx-auto px-4 py-16 text-center">Shop not found</div>;

  return (
    <div data-testid="shop-detail-page">
      <div className="relative h-56 sm:h-72 lg:h-80 overflow-hidden">
        <ImageSlider images={photoList(shop)} captions={shop.image_captions} alt={shop.name} overlayBottom="bottom-12 sm:bottom-16" className="w-full h-full" testid="shop-cover-slider">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent pointer-events-none" />
        </ImageSlider>
        <BackButton fallback="/category/shops" className="absolute top-3 left-3 sm:top-4 sm:left-6 z-20 inline-flex items-center gap-1.5 text-xs sm:text-sm text-white bg-black/55 hover:bg-black/70 backdrop-blur-sm rounded-full px-3 py-1.5 font-semibold shadow" testid="shop-back-btn" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="-mt-24 sm:-mt-28 relative">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-xl p-6 lg:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-2">
                  <Store className="w-3.5 h-3.5" /> Local Shop
                  <ShopStatusPill status={shop.status} shopId={id} size="lg" />
                </div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900" data-testid="shop-title">{shop.name}</h1>
                <div className="text-sm text-slate-500 mt-1">{shop.tagline}</div>
                <Link to={`/v/${shop.slug || id}`} className="inline-block mt-1 text-xs font-semibold text-emerald-700 hover:underline" data-testid="shop-full-page-link">View full shop page →</Link>
                <div className="flex items-center gap-4 mt-4 text-sm text-slate-600 flex-wrap">
                  <div className="flex items-center gap-1 bg-emerald-50 text-emerald-800 px-2 py-1 rounded font-semibold">
                    <Star className="w-3.5 h-3.5 fill-current" /> {shop.rating}
                  </div>
                  <span>{shop.reviews} reviews</span>
                  <div className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {shop.distance_km != null ? `${shop.distance_km} km` : "Distance unavailable"}{shop.location ? ` · ${shop.location}` : ""}</div>
                </div>
                <div className="mt-3 text-sm text-slate-600 space-y-1">
                  <div><span className="text-slate-400">Address:</span> {shop.address}</div>
                  {shop.landmark && <div><span className="text-slate-400">Landmark:</span> {shop.landmark}</div>}
                  {shop.phone && <div className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {shop.phone}</div>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <button
                  onClick={openDirections}
                  disabled={!shop.directions_url}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 text-sm font-semibold"
                  data-testid="get-directions-btn"
                  title={shop.directions_url ? "" : "Location not set by shop"}
                >
                  <Navigation className="w-4 h-4" /> Get Directions
                </button>
                {(shop.phone || shop.contact_locked) && <a href={shop.phone ? `tel:${shop.phone.replace(/\s+/g, "")}` : undefined} onClick={(e) => { if (contactGate(user, shop.contact_locked)) { e.preventDefault(); return; } trackShopContact(shop.shop_id, "call"); }} role="button" className="inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 hover:border-emerald-500 text-slate-800 px-5 py-2.5 text-sm font-semibold" data-testid="call-shop-btn">
                  <Phone className="w-4 h-4" /> Call
                </a>}
              </div>
            </div>

          </div>
        </div>

        {shop.business_profile_enabled && <BusinessProfileSections name={shop.name} profile={shop.business_profile} />}

        <div className="mt-12">
          <h2 className="font-display text-2xl font-bold mb-6">Products from this shop ({shop.products?.length || 0})</h2>
          {(!shop.products || shop.products.length === 0) ? (
            <div className="text-sm text-slate-500">No products listed yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
              {shop.products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
